import { Layout } from "antd";
import { Helmet } from "react-helmet";
import { Button, Result } from "antd";
import styled from "styled-components";
import { useEffect, useState } from "react";
import { useNetworkState } from "react-use";
import { UseQueryHookResult } from "@reduxjs/toolkit/dist/query/react/buildHooks";

interface ApiErrorTableComponentProps {
  refetch?: () => void;
  queryResult?: UseQueryHookResult<any> | any;
}

interface ErrorData {
  action?: () => void;
  status?: number | string;
  errorTitle?: string;
  actionText?: string;
  errorDescription?: string;
}

const ApiErrorTableComponentContainer = styled(Layout)`
  .ant-result svg {
    font-size: 50px;
  }

  .error-title {
    font-size: 16px;
    text-transform: initial;
  }

  .error-sub-title {
    text-transform: initial;
  }
`;

const ApiErrorTableComponent: React.FC<ApiErrorTableComponentProps> = ({
  refetch,
  queryResult,
}) => {
  const networkState = useNetworkState();
  const [errorData, setErrorData] = useState<ErrorData>(() => ({
    status: 500,
    actionText: "Refresh",
    errorTitle: "Error: Response Error",
    action: queryResult?.refetch || refetch,
    errorDescription:
      "There seems to be error for couple or reasons, check your internet or System Adminstrator",
  }));

  useEffect(() => {
    if (!networkState.online) {
      setErrorData({
        actionText: "Refresh",
        errorTitle: "Network Offline",
        status: queryResult?.error?.status,
        action: queryResult?.refetch || refetch,
        errorDescription: "please check your network and click the refresh button",
      });
    } else if (queryResult?.error?.status === "FETCH_ERROR") {
      setErrorData({
        actionText: "Refresh",
        errorTitle: "Server Disconnected",
        action: queryResult?.refetch || refetch,
        status: queryResult?.error?.status,
        errorDescription:
          "The development server has probably disconnected. Refresh the page if necessary and if problem persist please contact System Administrator",
      });
    } else if (queryResult?.error?.originalStatus === 404 || queryResult?.error?.status === 404) {
      setErrorData({
        status: 404,
        actionText: "Refresh",
        errorTitle: "Not Found",
        action: queryResult?.refetch || refetch,
        errorDescription: `The requested endpoint seems not to be found on this server. Please reach out to your Site Administrator`,
      });
    } else if (queryResult?.error?.status === 401 && queryResult?.error?.data?.code === 440) {
      const currentURL = window.location.href;

      setErrorData({
        actionText: "Reload",
        errorTitle: "Session timeout",
        status: queryResult?.error?.data?.code,
        action: () => (window.location.href = currentURL),
        errorDescription:
          "your session has expired kindly click on the reload button to reload the page and login using a new session",
      });
    } else if (
      queryResult?.error?.status === 401 &&
      queryResult?.error?.data?.error === "invalid_token"
    ) {
      setErrorData({
        actionText: "Refresh",
        status: queryResult?.error?.status,
        errorTitle: "Expired or invalid token",
        action: queryResult?.refetch || refetch,
        errorDescription:
          "your token seems to have expired please update token and click the refresh button",
      });
    } else if (queryResult?.error?.status === 400) {
      setErrorData({
        actionText: "Refresh",
        errorTitle: "Bad Request Error",
        status: queryResult?.error?.status,
        action: queryResult?.refetch || refetch,
        errorDescription:
          "The API request is probably malformed or illegal. Please check your request and verify if the API URL is accurate.",
      });
    } else if (
      queryResult?.error?.status === 500 &&
      queryResult?.error?.data?.code === "INTERNAL_SERVER_ERROR"
    ) {
      const errorDescription = queryResult?.error?.data?.description
        ? queryResult?.error?.data?.description
        : "An internal server error has occurred. Please try again. If the issue persists, please contact the System Administrator";

      setErrorData({
        errorDescription,
        actionText: "Refresh",
        status: queryResult?.error?.status,
        errorTitle: "Internal Server Error",
        action: queryResult?.refetch || refetch,
      });
    } else if (
      networkState.online &&
      queryResult?.error?.status === "PARSING_ERROR" &&
      queryResult?.error?.originalStatus === 500
    ) {
      const description = `${
        queryResult?.error?.data && queryResult?.error?.data?.toLowerCase().includes("proxy error")
          ? "Proxy Error: Unable to load, check if your proxy or VPN is well connected and refresh page"
          : `${queryResult?.error?.error})}`
      }`;

      setErrorData({
        actionText: "Refresh",
        errorDescription: description,
        errorTitle: "Proxy | VPN Error",
        action: queryResult?.refetch || refetch,
        status: queryResult?.error?.originalStatus,
      });
    }
  }, [queryResult?.requestId, queryResult?.error, networkState.online, refetch]);

  return (
    <ApiErrorTableComponentContainer>
      <>
        <Helmet>
          <title>NEFT Settlement | Error Page</title>
          <meta name="description" content="Helmet application" />
        </Helmet>

        <Result
          status="error"
          style={{
            alignItems: "center",
            justifyContent: "center",
          }}
          title={
            <span className="error-title">{`${errorData?.status}: ${errorData?.errorTitle}`}</span>
          }
          subTitle={<span className="error-sub-title">{errorData?.errorDescription}</span>}
          extra={
            <Button type="primary" onClick={() => errorData?.action()}>
              {errorData?.actionText}
            </Button>
          }
        />
      </>
    </ApiErrorTableComponentContainer>
  );
};

export default ApiErrorTableComponent;
